import React, { useState } from "react";
import Button from "Components/button/Button";
import Select from "Components/select/Select";
import DropdownIndicator from "Components/customArrowSelect/CustomArrowSelect";
import InputPhone from "Components/inputPhone/InputPhone";
import t from "Utils/translation";
import Input from "Components/input/Input";
import { province, allCountries, euCountries } from "@/config";
import { verifyFiscalcode } from "Modules/orders";
import InputVerify from "Components/inputVerify/InputVerify";
import _ from "lodash";
import isEmail from "validator/lib/isEmail";

const Billing = ({ formData, formErrors, setFormFieldValue, setFormErrors, onFormCancel, nextStep, stepper }) => {
  const [isFiscalCodeVerified, setIsFiscalCodeVerified] = useState(false);
  const [isVerifingFiscalCode, setIsVerifingFiscalCode] = useState(false);

  const isVATRequired = formData.customer_type !== "private" && formData.country === "IT";
  const showVAT = euCountries.includes(formData.country) && formData.customer_type !== "private";
  const showCompanyFields = formData.customer_type === "company" || formData.customer_type === "reseller";

  const checkFiscalCode = async (code) => {
    setIsVerifingFiscalCode(true);
    try {
      await verifyFiscalcode(_.trim(code));
      setFormErrors({ fiscal_code: null });
      setIsFiscalCodeVerified(true);
    } catch (error) {
      setFormErrors({ fiscal_code: t("status.migrationInvalid") });
      setIsFiscalCodeVerified(false);
    }
    setIsVerifingFiscalCode(false);
  };

  const firstname = "piero";

  const validateAndNext = async () => {
    let errors = {};
    let requireds = ["firstname", "lastname", "address1", "city", "state", "postcode", "phonenumber"];

    if (isVATRequired && showVAT) {
      requireds.push("vat");

      if (formData.vat?.length <= 2) {
        errors.vat = t("status.migrationInvalid");
      }
    }

    if (formData.customer_type !== "private") {
      requireds.push("companyname");
    } else {
      requireds.push("fiscalcode");

      if (!isFiscalCodeVerified) {
        errors.fiscalcode = t("errorMsg.click.toVerify");
      }
    }

    if (formData.recipientCode && formData.recipientCode.length != 7 && !isEmail(formData.recipientCode)) {
      errors.recipientCode = t("errorMsg.notValid.recipientCode");
    }

    requireds.forEach((field) => {
      if (!formData[field]) errors[field] = t("input.error.empity");
    });

    console.log("🚀 ~ file: BillingInfo.jsx:14 ~ Billing ~ errors", errors);

    if (Object.keys(errors).length === 0) {
      nextStep();
    } else {
      setFormErrors(errors);
    }
  };

  return (
    <div className="flex flex-col items-center h-full p-4 pb-0">
      {stepper}

      <div className="mt-4 w-full max-w-[--form-page-width]">
        <div className="sectionTitle">{t("delivery.accounts.add.stepBillingInfo.title")}</div>
        <div className="sectionDesc">{t("delivery.accounts.add.stepBillingInfo.desc")}</div>
        <Button docsKey="" className="btnDocsFP" color="docs">
          button.docs
        </Button>
      </div>

      <div className="formContainer">
        <div className="flex flex-col w-full">
          {formData.customer_type !== "private" && (
            <div className="mt-9">
              <Input
                name="companyname"
                required
                label={t("input.label.company")}
                errors={formErrors.companyname}
                value={formData.companyname}
                onChange={setFormFieldValue}
                placeholder={t("input.placeholder.company")}
              />
            </div>
          )}

          <div className={formData.customer_type === "private" ? "mt-9" : "mt-4"}>
            <Input
              name="firstname"
              label={t("input.label.firstName")}
              initialValue={firstname}
              value={formData.firstname}
              errors={formErrors.firstname}
              onChange={setFormFieldValue}
              placeholder={t("input.placeholder.firstName")}
            />
          </div>

          <div className="mt-4">
            <Input
              name="lastname"
              label={t("input.label.lastName")}
              value={formData.lastname}
              onChange={setFormFieldValue}
              errors={formErrors.lastname}
              placeholder={t("input.placeholder.lastName")}
            />
          </div>

          {showCompanyFields && (
            <div className="mt-12">
              <Input
                name="vat"
                label={t("input.label.vat")}
                required={isVATRequired}
                value={formData.vat}
                onChange={setFormFieldValue}
                placeholder={t("input.placeholder.vat")}
              />
            </div>
          )}

          {showCompanyFields && formData.country === "IT" && (
            <div className="mt-4">
              <Input
                name="fiscalcode"
                label={t("input.label.fiscalcode2")}
                placeholder={t("input.placeholder.fiscalcode")}
                value={formData.fiscalcode}
                errors={formErrors.fiscalcode}
                onChange={setFormFieldValue}
              />
            </div>
          )}

          {formData.customer_type === "private" && (
            <div className={formData.customer_type === "private" ? "mt-12" : "mt-4"}>
              <InputVerify
                name="fiscalcode"
                label={t("input.label.fiscalcode")}
                value={formData.fiscalcode}
                onChange={setFormFieldValue}
                placeholder={t("input.placeholder.fiscalcode")}
                isVerified={isFiscalCodeVerified}
                loading={isVerifingFiscalCode}
                onClick={checkFiscalCode}
                errors={formErrors.fiscalcode}
                onBlur={() => {
                  if (formData.fiscalcode) {
                    checkFiscalCode(formData.fiscalcode);
                  }
                }}
              />
            </div>
          )}

          {showCompanyFields && formData.country === "IT" && (
            <div className="mt-12">
              <Input
                name="recipient_code"
                label={t("input.label.recipientCode")}
                value={formData.recipient_code}
                onChange={setFormFieldValue}
                placeholder={t("input.placeholder.recipientCode")}
              />
            </div>
          )}

          <div className="flex flex-col w-full gap-4 mt-12 md:flex-row md:gap-2">
            <div className="flex flex-col w-full gap-2">
              <Input
                name="address1"
                label={t("input.label.address")}
                value={formData.address1}
                onChange={setFormFieldValue}
                errors={formErrors.address1}
                placeholder={t("input.placeholder.address")}
              />

              <Input
                name="city"
                label={t("input.label.city")}
                value={formData.city}
                onChange={setFormFieldValue}
                errors={formErrors.city}
                placeholder={t("input.placeholder.city")}
              />

              <Select
                name="country"
                label={t("input.label.country")}
                value={formData.country}
                onChange={setFormFieldValue}
                placeholder={t("input.placeholder.country")}
                components={{ DropdownIndicator }}
                clearable={false}
                options={allCountries}
              />
            </div>

            <div className="flex flex-col w-full gap-2">
              <Input
                name="postcode"
                label={t("input.label.postcode")}
                value={formData.postcode}
                onChange={setFormFieldValue}
                errors={formErrors.postcode}
                placeholder={t("input.placeholder.postcode")}
              />

              {formData.country === "IT" ? (
                <Select
                  label={t("input.label.state")}
                  placeholder={t("input.placeholder.city")}
                  components={{ DropdownIndicator }}
                  name="state"
                  options={province}
                  clearable={false}
                  value={formData.state}
                  errors={formErrors.state}
                  onChange={setFormFieldValue}
                  required
                />
              ) : (
                <Input
                  name="state"
                  label={t("input.label.state")}
                  errors={formErrors.state}
                  value={formData.state}
                  onChange={setFormFieldValue}
                  placeholder={t("input.placeholder.state")}
                />
              )}

              <InputPhone
                label={t("input.label.telephone")}
                placeholder={t("input.placeholder.telephone")}
                name="phonenumber"
                required
                onChange={setFormFieldValue}
                errors={formErrors.phonenumber}
                value={formData.phonenumber}
              />
            </div>
          </div>
        </div>

        <div className="flexColumnContainer">
          <div className="formFooter">
            <Button color="text" type="button" onClick={onFormCancel}>
              button.cancel
            </Button>
            <Button color="accent" onClick={validateAndNext}>
              button.continue
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Billing;
